import styled from '@emotion/styled';
import type { MouseEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { themeVars } from 'src/ui-components/theme/theme';

import Arrow from '../../../assets/icons/menu/arrow.svg';
import { FrontTooltip } from '../../../ui-components';
import { MenuIcon, MenuItem, NavTitle } from '../helpers/styled';
import type { ISidebarNav } from '../helpers/types';
import { useIsChildOpen } from '../hooks/useIsChildOpen';
import { useIsSidebarActive } from '../hooks/useIsSidebarActive';
import { useSidebarStore } from '../store/store';
import type { IFrontMenuChildCollapsed } from './FrontMenuChildCollapsed';
import { FrontMenuChildCollapsed } from './FrontMenuChildCollapsed';

interface IProps {
  item: ISidebarNav;
  isChild?: boolean;
}

export const FrontMenuItem = ({ item, isChild }: IProps) => {
  const navigate = useNavigate();
  const { collapsed, setIsChildOpen } = useSidebarStore();
  const { isSidebarActive } = useIsSidebarActive();
  const isChildOpen = useIsChildOpen();
  const withChildren = useMemo(
    () => item.children !== undefined && item.children.length > 0,
    [item.children],
  );

  const isActive = useMemo(
    () => (collapsed || !withChildren || !isChildOpen(item.key)) && isSidebarActive(item),
    [collapsed, withChildren, isChildOpen, item, isSidebarActive],
  );

  const popoverRef = useRef<IFrontMenuChildCollapsed>(null);

  const handleOpenPopover = (event: MouseEvent<HTMLDivElement>) => {
    if (popoverRef.current) {
      popoverRef.current.openPopover(event);
    }
  };

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (withChildren) {
        !collapsed
          ? setIsChildOpen({ key: item.key, value: !isChildOpen(item.key) })
          : handleOpenPopover(event);
      } else {
        navigate(item.link || '');
      }
    },
    [collapsed, isChildOpen, item, navigate, setIsChildOpen, withChildren],
  );

  return (
    <>
      <MenuItem
        $isActive={isActive}
        $isCollapsed={collapsed}
        $isChild={isChild}
        onClick={handleClick}
      >
        {item.icon && (
          <FrontTooltip title={item.title} disabled={!collapsed} placement={'right'}>
            <MenuIcon $isActive={isActive} $src={item.icon} />
          </FrontTooltip>
        )}
        {!collapsed && <NavTitle> {item.title} </NavTitle>}
        {!collapsed && withChildren && (
          <ArrowIcon $isOpen={isChildOpen(item.key)} $isActive={isActive} />
        )}
      </MenuItem>
      {collapsed && withChildren && <FrontMenuChildCollapsed item={item} ref={popoverRef} />}
    </>
  );
};

const ArrowIcon = styled.div<{ $isOpen?: boolean; $isActive?: boolean }>`
  width: 10px;
  height: 10px;
  mask: url(${Arrow}) no-repeat center / contain;
  background-color: ${({ $isActive }) => ($isActive ? themeVars.colors.titan25 : '#504c55')};
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : 0)});
  transition: transform 0.3s;
`;
